import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, TrendingUp, DollarSign, Activity, Building, MapPin, Award,
  CheckCircle, ChevronRight, Stethoscope, ShieldAlert, ListChecks, HeartPulse, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ConsultationRequestDialog from '../components/ConsultationRequestDialog';

const slugify = (name) => (name || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export default function ProcedureDetail() {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const [proc, setProc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/treatments/${procedureId}`);
        if (!res.ok) throw new Error('Procedure not found');
        const data = await res.json();
        setProc(data.treatment);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
  }, [procedureId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="loading-state">
          <div className="text-6xl mb-4 animate-pulse">🩺</div>
          <p className="text-lg text-gray-600">Loading procedure details...</p>
        </div>
      </div>
    );
  }

  if (error || !proc) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center" data-testid="error-state">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Procedure Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "The procedure you're looking for doesn't exist."}</p>
          <Button onClick={() => navigate('/treatments')} data-testid="back-to-treatments-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Treatments
          </Button>
        </div>
      </div>
    );
  }

  const rc = proc.rich_content || {};
  const specialtySlug = slugify(proc.category || proc.specialty);

  return (
    <div className="min-h-screen bg-white" data-testid="procedure-detail-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-orange-100 z-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🩹</span>
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">MedRahi</h1>
              <p className="text-xs text-gray-600">Healthcare Without Borders</p>
            </div>
          </div>
          <Button variant="ghost" onClick={() => navigate(`/treatments/${specialtySlug}`)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to {proc.category}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50" data-testid="hero-section">
        <div className="mx-auto max-w-7xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap" data-testid="breadcrumb">
            <button onClick={() => navigate('/treatments')} className="hover:text-orange-600">Treatments</button>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => navigate(`/treatments/${specialtySlug}`)} className="hover:text-orange-600">{proc.category}</button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{proc.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Activity className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <Badge className="mb-3 bg-orange-100 text-orange-800" data-testid="procedure-category">{proc.category}</Badge>
                  <h1 className="font-heading text-4xl font-bold text-gray-900 mb-4" data-testid="procedure-name">{proc.name}</h1>
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge variant="outline" className="px-3 py-1">{proc.procedure_type}</Badge>
                    {proc.is_surgical && <Badge variant="outline" className="px-3 py-1 border-orange-300 text-orange-700">Surgical Procedure</Badge>}
                  </div>
                  <Button
                    className="mt-6 bg-gradient-to-r from-orange-500 to-red-600 text-white"
                    onClick={() => setConsultOpen(true)}
                    data-testid="request-consultation-btn"
                  >
                    <Stethoscope className="w-4 h-4 mr-2" /> Request a Consultation
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-orange-200 shadow-lg" data-testid="quick-stats-card">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Procedure Info</h3>
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <Clock className="w-5 h-5 text-orange-600" />
                    <div className="flex-1"><p className="text-xs text-gray-600">Duration</p><p className="font-semibold text-gray-900" data-testid="procedure-duration">{proc.duration}</p></div>
                  </div>
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <Activity className="w-5 h-5 text-orange-600" />
                    <div className="flex-1"><p className="text-xs text-gray-600">Recovery Time</p><p className="font-semibold text-gray-900" data-testid="procedure-recovery">{proc.recovery_time}</p></div>
                  </div>
                  <div className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <div className="flex-1"><p className="text-xs text-gray-600">Success Rate</p><p className="font-semibold text-green-600" data-testid="procedure-success">{proc.success_rate}%</p></div>
                  </div>
                  <div className="flex items-center gap-3 py-2">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                    <div className="flex-1"><p className="text-xs text-gray-600">Estimated Cost</p><p className="font-semibold text-gray-900" data-testid="procedure-cost">{proc.cost_range}</p></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Rich content */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="mx-auto max-w-4xl space-y-12">
          {rc.overview && (
            <div data-testid="procedure-overview">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Overview</h2>
              {rc.overview.split('\n').filter(Boolean).map((p, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-3">{p}</p>
              ))}
            </div>
          )}

          {Array.isArray(rc.what_it_treats) && rc.what_it_treats.length > 0 && (
            <div data-testid="procedure-treats">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2"><ListChecks className="w-6 h-6 text-orange-600" /> What it treats</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {rc.what_it_treats.map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" /> <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(rc.what_to_expect) && rc.what_to_expect.length > 0 && (
            <div data-testid="procedure-expect">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">What to expect</h2>
              <div className="space-y-5">
                {rc.what_to_expect.map((step, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                      <p className="text-gray-600 mt-1">{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(rc.preparation || rc.recovery_details) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {rc.preparation && (
                <Card className="border-orange-100"><CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-orange-600" /> Preparation</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{rc.preparation}</p>
                </CardContent></Card>
              )}
              {rc.recovery_details && (
                <Card className="border-orange-100"><CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><HeartPulse className="w-5 h-5 text-orange-600" /> Recovery journey</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{rc.recovery_details}</p>
                </CardContent></Card>
              )}
            </div>
          )}

          {rc.why_india && (
            <div className="rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 p-8 text-white" data-testid="procedure-why-india">
              <h2 className="font-heading text-2xl font-bold mb-3 flex items-center gap-2"><Sparkles className="w-6 h-6" /> Why choose India</h2>
              <p className="text-white/90 leading-relaxed">{rc.why_india}</p>
            </div>
          )}

          {Array.isArray(rc.faqs) && rc.faqs.length > 0 && (
            <div data-testid="procedure-faqs">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
              <Accordion type="single" collapsible className="w-full">
                {rc.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-medium text-gray-900">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Disclaimer */}
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="procedure-disclaimer">
            <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>This content is for general information only and is not medical advice or a diagnosis. Treatment suitability, costs and outcomes vary by individual — please consult a qualified doctor. Costs shown are indicative.</p>
          </div>
        </div>
      </section>

      {/* Hospitals */}
      {proc.hospitals && proc.hospitals.length > 0 && (
        <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="hospitals-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">Hospitals offering {proc.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {proc.hospitals.slice(0, 6).map((h, i) => (
                <Card key={i} className="hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate(`/hospitals/${slugify(h.name)}`)} data-testid={`hospital-card-${i}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0"><Building className="w-6 h-6 text-white" /></div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{h.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-gray-600"><MapPin className="w-3 h-3" /><span>{h.city}{h.state ? `, ${h.state}` : ''}</span></div>
                      </div>
                    </div>
                    {h.accreditation && <Badge className="text-xs bg-orange-100 text-orange-800"><Award className="w-3 h-3 mr-1" />{h.accreditation}</Badge>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-3">Considering {proc.name}?</h2>
          <p className="text-gray-600 mb-6">Speak with our medical coordinators for a personalised plan, cost estimate and hospital options.</p>
          <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-600 text-white" onClick={() => setConsultOpen(true)} data-testid="cta-request-consultation-btn">
            <Stethoscope className="w-5 h-5 mr-2" /> Request a Consultation
          </Button>
        </div>
      </section>

      <ConsultationRequestDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        context={{ type: 'treatment', name: proc.name, treatmentCode: proc.name }}
      />
    </div>
  );
}
